import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { exportData } from '../utils/exportUtils';
import ExportButton from '../components/common/ExportButton';
import { buildEngagementFilter, isBotUserAgent, filterAudienceEvents } from '../utils/emailBotDetection';
import { buildDeferredStatus } from '../utils/emailDeferredStatus';
import { useAuth } from '../components/auth/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { canViewAllEmail } from '../utils/emailAccess';
import { isAssignedCustomer } from '../utils/reportOwnership';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// BCC/verification copies (campaign fromEmail, replyTo, campaignBccEmails system setting)
// are internal addresses, not real audience members — they must never appear in analytics.
// Prefer the isBccCopy flag (tagged at write time from the campaign's persisted bccEmails
// list); fall back to matching campaign.bccEmails/fromEmail/replyTo directly for events
// written before that flag existed.
function buildBccEmailSet(campaign) {
  return new Set([
    ...(Array.isArray(campaign?.bccEmails) ? campaign.bccEmails : []),
    campaign?.fromEmail,
    campaign?.replyTo
  ].filter(Boolean).map(e => e.toLowerCase()));
}

function getTimeRangeCutoff(range) {
  if (range === 'all') return null;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - parseInt(range.replace('d', '')));
  cutoff.setHours(0, 0, 0, 0);
  return cutoff;
}

const CampaignAnalyticsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const viewAllEmail = canViewAllEmail(currentUser, can);
  const [loadError, setLoadError] = useState('');
  
  const [campaign, setCampaign] = useState(null);
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all'); // all, 7d, 30d
  const [customers, setCustomers] = useState([]);
  const loadVersion = useRef(0);
  
  // Recipients table state
  const [recipientSearch, setRecipientSearch] = useState('');
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  const [engagementFilter, setEngagementFilter] = useState('all'); // all, opened, clicked, bounced, unopened

  useEffect(() => {
    loadCampaignData();
    return () => { loadVersion.current += 1; };
  }, [campaignId, timeRange, currentUser, viewAllEmail]);

  const loadCampaignData = async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError('');
    setCampaign(null);
    setEvents([]);
    setAnalytics(null);
    setCustomers([]);
    try {
      // Load campaign
      const campaignDoc = await getDoc(doc(db, collectionPath('emailCampaigns'), campaignId));
      if (version !== loadVersion.current) return;
      if (!campaignDoc.exists()) {
        setLoading(false);
        return;
      }
      
      const campaignData = { id: campaignDoc.id, ...campaignDoc.data() };
      if (!viewAllEmail && campaignData.createdBy !== currentUser?.uid) {
        throw new Error('You can only view analytics for your own campaigns.');
      }
      setCampaign(campaignData);

      // BEST PRACTICE: Use asynchronous parallel calls to minimize latency
      // Load customers AND events in parallel (no data dependency)
      const [customersSnap, eventsSnap] = await Promise.all([
        // BEST PRACTICE: Add limit to prevent hotspotting on high read rates
        // Limit to 5000 customers (should cover most use cases)
        getDocs(query(
          collection(db, collectionPath('customers')),
          limit(5000)
        )),
        // NOTE: Requires composite index on emailEvents: campaignId (asc) + timestamp (desc)
        // This query will fail until index is created via Firebase Console
        getDocs(query(
          collection(db, collectionPath('emailEvents')),
          where('campaignId', '==', campaignId),
          orderBy('timestamp', 'desc'),
          limit(10000) // Limit events to prevent excessive reads
        ))
      ]);
      if (version !== loadVersion.current) return;

      const customersData = customersSnap.docs.map(doc => {
        const data = doc.data();
        return {
          firestoreId: doc.id,  // The Firestore document ID
          ...data
        };
      });
      const visibleCustomers = viewAllEmail ? customersData : customersData.filter(customer => isAssignedCustomer(customer, currentUser));
      setCustomers(visibleCustomers);

      const eventsData = eventsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : new Date(doc.data().timestamp)
      }));
      setEvents(eventsData);
      
      // Calculate analytics
      const analyticsData = calculateAnalytics(campaignData, eventsData, visibleCustomers);
      setAnalytics(analyticsData);
      
      setLoading(false);
    } catch (error) {
      if (version !== loadVersion.current) return;
      console.error('Failed to load campaign analytics:', error);
      setLoadError(error.message);
      setCampaign(null);
      setLoading(false);
    }
  };

  const calculateAnalytics = (campaign, events, customersData) => {
    // BCC/verification copies must never appear anywhere in analytics — not in the
    // aggregate Sent/Delivered/etc. counts and not as a row in the recipient table.
    const bccEmailSet = buildBccEmailSet(campaign);
    const audienceEvents = filterAudienceEvents(events, bccEmailSet);

    // Filter by time range
    const cutoff = getTimeRangeCutoff(timeRange);
    const filteredEvents = cutoff
      ? audienceEvents.filter(e => e.timestamp >= cutoff)
      : audienceEvents;

    // Build set of bounced emails to exclude their false open/click events
    const { isHumanEngagement, bouncedEmails } = buildEngagementFilter(filteredEvents);

    // Calculate stats from actual events (deduplicate by recipient email)
    const uniqueOpensSet = new Set();
    const uniqueClicksSet = new Set();
    const machineOpensSet = new Set();
    const uniqueSentSet = new Set();
    const uniqueDeliveredSet = new Set();
    const uniqueBouncedSet = new Set();
    const uniqueBlockedSet = new Set();
    const uniqueDroppedSet = new Set();
    const uniqueDeferredSet = new Set();
    const uniqueUnsubSet = new Set();
    let totalOpens = 0;
    let totalClicks = 0;
    let totalMachineOpens = 0;
    
    filteredEvents.forEach(event => {
      if (event.eventType === 'sent' && event.recipientEmail) uniqueSentSet.add(event.recipientEmail);
      if (event.eventType === 'delivered' && event.recipientEmail) uniqueDeliveredSet.add(event.recipientEmail);
      if (event.eventType === 'bounced' && event.recipientEmail) uniqueBouncedSet.add(event.recipientEmail);
      if (event.eventType === 'blocked' && event.recipientEmail) uniqueBlockedSet.add(event.recipientEmail);
      if (event.eventType === 'dropped' && event.recipientEmail) uniqueDroppedSet.add(event.recipientEmail);
      if (event.eventType === 'deferred' && event.recipientEmail) uniqueDeferredSet.add(event.recipientEmail);
      if (event.eventType === 'unsubscribed' && event.recipientEmail) uniqueUnsubSet.add(event.recipientEmail);
      if (event.eventType === 'opened') {
        if (isHumanEngagement(event)) {
          totalOpens++;
          if (event.recipientEmail) uniqueOpensSet.add(event.recipientEmail);
        } else if (!bouncedEmails.has(event.recipientEmail)) {
          // Machine open (not bounced)
          totalMachineOpens++;
          if (event.recipientEmail) machineOpensSet.add(event.recipientEmail);
        }
      }
      if (event.eventType === 'clicked' && isHumanEngagement(event)) {
        totalClicks++;
        if (event.recipientEmail) uniqueClicksSet.add(event.recipientEmail);
      }
    });
    
    // Subtract bounced/blocked/dropped recipients from delivered
    const effectiveDelivered = new Set([...uniqueDeliveredSet].filter(e => !uniqueBouncedSet.has(e) && !uniqueBlockedSet.has(e) && !uniqueDroppedSet.has(e)));
    
    // Pending retry = deferred AND not yet resolved AND most recent deferred
    // is still within SendGrid's 72h retry window (+ buffer). Older deferrals
    // become "expired" instead of staying pending forever.
    const { pendingRetryEmails, expiredDeferredEmails } = buildDeferredStatus(filteredEvents);
    
    const stats = {
      sent: uniqueSentSet.size,
      delivered: effectiveDelivered.size,
      opens: totalOpens,
      uniqueOpens: uniqueOpensSet.size,
      clicks: totalClicks,
      uniqueClicks: uniqueClicksSet.size,
      bounces: uniqueBouncedSet.size,
      blocked: uniqueBlockedSet.size,
      dropped: uniqueDroppedSet.size,
      deferred: pendingRetryEmails.size,
      expiredDeferred: expiredDeferredEmails.size,
      unsubscribes: uniqueUnsubSet.size,
      machineOpens: totalMachineOpens,
      uniqueMachineOpens: machineOpensSet.size
    };
    
    // Timeline data
    const timeline = {};
    filteredEvents.forEach(event => {
      const date = event.timestamp.toISOString().split('T')[0];
      if (!timeline[date]) {
        timeline[date] = { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 };
      }
      if (event.eventType === 'sent') timeline[date].sent++;
      if (event.eventType === 'delivered') timeline[date].delivered++;
      if (event.eventType === 'opened' && isHumanEngagement(event)) timeline[date].opened++;
      if (event.eventType === 'clicked' && isHumanEngagement(event)) timeline[date].clicked++;
      if (event.eventType === 'bounced') timeline[date].bounced++;
    });

    // Top clicked links
    const links = {};
    filteredEvents
      .filter(e => e.eventType === 'clicked' && e.metadata?.clickedUrl && isHumanEngagement(e))
      .forEach(event => {
        const url = event.metadata.clickedUrl;
        links[url] = (links[url] || 0) + 1;
      });
    
    const topLinks = Object.entries(links)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([url, clicks]) => ({ url, clicks }));

    // Opens by hour (exclude bounced recipients and machine opens)
    const opensByHour = Array(24).fill(0);
    filteredEvents
      .filter(e => e.eventType === 'opened' && isHumanEngagement(e))
      .forEach(event => {
        opensByHour[event.timestamp.getHours()]++;
      });

    // Device breakdown
    const devices = { mobile: 0, desktop: 0, tablet: 0, unknown: 0 };
    filteredEvents
      .filter(e => e.eventType === 'opened' && e.metadata?.userAgent && isHumanEngagement(e))
      .forEach(event => {
        const ua = event.metadata.userAgent.toLowerCase();
        if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
          devices.mobile++;
        } else if (ua.includes('tablet') || ua.includes('ipad')) {
          devices.tablet++;
        } else if (ua.includes('mozilla') || ua.includes('chrome') || ua.includes('safari')) {
          devices.desktop++;
        } else {
          devices.unknown++;
        }
      });

    // Top 10 Customers by Engagement in this campaign
    const customerEngagement = {};
    filteredEvents.forEach(event => {
      const customerId = event.recipientCustomerId;
      if (!customerId) return;
      // Skip bounced recipients and machine opens
      if (!isHumanEngagement(event)) return;
      
      if (!customerEngagement[customerId]) {
        customerEngagement[customerId] = {
          customerId,
          customerName: 'Unknown Customer',
          opens: 0,
          clicks: 0,
          totalEngagement: 0
        };
      }
      
      if (event.eventType === 'opened') {
        customerEngagement[customerId].opens++;
        customerEngagement[customerId].totalEngagement++;
      }
      if (event.eventType === 'clicked') {
        customerEngagement[customerId].clicks++;
        customerEngagement[customerId].totalEngagement++;
      }
    });

    const topCustomers = Object.values(customerEngagement)
      .sort((a, b) => b.totalEngagement - a.totalEngagement)
      .slice(0, 10);
    
    // Resolve customer names after sorting
    if (customersData && customersData.length > 0) {
      topCustomers.forEach(tc => {
        // Try multiple matching strategies
        const customer = customersData.find(c => 
          c.firestoreId === tc.customerId ||  // Match by Firestore document ID
          c.customerId === tc.customerId ||    // Match by customerId field
          c.id === tc.customerId ||           // Match by id field
          c._oldDocId === tc.customerId       // Match by _oldDocId field
        );
        if (customer) {
          tc.customerName = customer.customerName || 'Unknown Customer';
        }
      });
    }

    // Top 10 Email Addresses by Engagement in this campaign
    const emailEngagement = {};
    filteredEvents.forEach(event => {
      const email = event.recipientEmail;
      if (!email) return;
      // Skip bounced recipients and machine opens
      if (!isHumanEngagement(event)) return;
      
      if (!emailEngagement[email]) {
        emailEngagement[email] = {
          email,
          customerId: event.recipientCustomerId,
          customerName: 'Unknown',
          opens: 0,
          clicks: 0,
          totalEngagement: 0,
          lastActivity: event.timestamp
        };
      }
      
      if (event.eventType === 'opened') {
        emailEngagement[email].opens++;
        emailEngagement[email].totalEngagement++;
      }
      if (event.eventType === 'clicked') {
        emailEngagement[email].clicks++;
        emailEngagement[email].totalEngagement++;
      }
      
      // Track most recent activity
      if (event.timestamp > emailEngagement[email].lastActivity) {
        emailEngagement[email].lastActivity = event.timestamp;
      }
    });

    const topEmails = Object.values(emailEngagement)
      .sort((a, b) => b.totalEngagement - a.totalEngagement)
      .slice(0, 10);
    
    // Resolve customer names after sorting
    if (customersData && customersData.length > 0) {
      topEmails.forEach(te => {
        if (te.customerId) {
          // Try multiple matching strategies
          const customer = customersData.find(c => 
            c.firestoreId === te.customerId ||  // Match by Firestore document ID
            c.customerId === te.customerId ||    // Match by customerId field
            c.id === te.customerId ||           // Match by id field
            c._oldDocId === te.customerId       // Match by _oldDocId field
          );
          if (customer) {
            te.customerName = customer.customerName || 'Unknown';
          }
        }
      });
    }

    return {
      stats,
      timeline,
      topLinks,
      opensByHour,
      devices,
      topCustomers,
      topEmails
    };
  };

  // Aggregate recipient-level engagement data
  const recipientData = useMemo(() => {
    const recipients = {};

    // BCC/verification copies must never appear as a recipient row.
    const bccEmailSet = buildBccEmailSet(campaign);
    const audienceEvents = filterAudienceEvents(events, bccEmailSet);
    const cutoff = getTimeRangeCutoff(timeRange);
    const rangedEvents = cutoff
      ? audienceEvents.filter(e => e.timestamp >= cutoff)
      : audienceEvents;
    
    // Use shared bot detection across all event types
    const { isHumanEngagement, bouncedEmails } = buildEngagementFilter(rangedEvents);

    // Determine which deferred recipients are still within SendGrid's 72h
    // retry window vs. expired (no longer being retried).
    const { pendingRetryEmails, expiredDeferredEmails } = buildDeferredStatus(rangedEvents);
    
    rangedEvents.forEach(event => {
      const email = event.recipientEmail;
      if (!email) return;
      
      if (!recipients[email]) {
        recipients[email] = {
          email,
          customerId: event.recipientCustomerId,
          customerName: '',
          sent: 0,
          delivered: 0,
          opens: 0,
          clicks: 0,
          machineOpens: 0,
          bounced: false,
          blocked: false,
          dropped: false,
          deferred: false,
          deferredExpired: false,
          unsubscribed: false,
          lastActivity: null,
          clickedUrls: []
        };
      }
      
      const recipient = recipients[email];
      const isHuman = isHumanEngagement(event);
      
      // Track event types
      if (event.eventType === 'sent') recipient.sent++;
      if (event.eventType === 'delivered') recipient.delivered++;
      if (event.eventType === 'opened') {
        if (isHuman) {
          recipient.opens++;
        } else if (!bouncedEmails.has(email)) {
          recipient.machineOpens++;
        }
      }
      if (event.eventType === 'clicked') {
        if (isHuman) {
          recipient.clicks++;
          if (event.metadata?.clickedUrl && !recipient.clickedUrls.includes(event.metadata.clickedUrl)) {
            recipient.clickedUrls.push(event.metadata.clickedUrl);
          }
        }
      }
      if (event.eventType === 'bounced') recipient.bounced = true;
      if (event.eventType === 'blocked') recipient.blocked = true;
      if (event.eventType === 'dropped') recipient.dropped = true;
      if (event.eventType === 'deferred') {
        // Only flag as still-pending if SendGrid is still in its retry window.
        recipient.deferred = pendingRetryEmails.has(email);
        if (expiredDeferredEmails.has(email)) recipient.deferredExpired = true;
      }
      if (event.eventType === 'unsubscribed') recipient.unsubscribed = true;
      
      // Track last activity
      if (!recipient.lastActivity || event.timestamp > recipient.lastActivity) {
        recipient.lastActivity = event.timestamp;
      }
    });
    
    // Add customer names
    Object.values(recipients).forEach(recipient => {
      if (recipient.customerId) {
        // Try matching against both firestoreId and id field
        const customer = customers.find(c => 
          c.firestoreId === recipient.customerId || 
          c.id === recipient.customerId
        );
        if (customer) {
          recipient.customerName = customer.customerName || customer.name || '';
        }
      }
      
      // Discard open/click counts for bounced recipients — these are false positives
      // from bots, security scanners, or mail server prefetching during deferral attempts
      if (recipient.bounced) {
        recipient.opens = 0;
        recipient.clicks = 0;
        recipient.clickedUrls = [];
      }
    });
    
    return Object.values(recipients);
  }, [events, customers, campaign, timeRange]);

  // Filter and sort recipients
  const filteredRecipients = useMemo(() => {
    let filtered = recipientData;
    
    // Search filter
    if (recipientSearch) {
      const searchLower = recipientSearch.toLowerCase();
      filtered = filtered.filter(r => 
        r.email?.toLowerCase().includes(searchLower) ||
        r.customerName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Engagement filter
    if (engagementFilter !== 'all') {
      if (engagementFilter === 'opened') {
        filtered = filtered.filter(r => r.opens > 0);
      } else if (engagementFilter === 'clicked') {
        filtered = filtered.filter(r => r.clicks > 0);
      } else if (engagementFilter === 'bounced') {
        filtered = filtered.filter(r => r.bounced);
      } else if (engagementFilter === 'blocked') {
        filtered = filtered.filter(r => r.blocked);
      } else if (engagementFilter === 'dropped') {
        filtered = filtered.filter(r => r.dropped);
      } else if (engagementFilter === 'unopened') {
        filtered = filtered.filter(r => r.opens === 0 && r.delivered > 0);
      }
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // String comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [recipientData, recipientSearch, engagementFilter, sortField, sortDirection]);

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Export analytics in multiple formats
  const handleExport = (format) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const baseFilename = `campaign_analytics_${campaign.name.replace(/\s+/g, '_')}_${timestamp}`;
    
    const headers = [
      'Recipient Email',
      'Customer Name',
      'Customer ID',
      'Sent',
      'Delivered',
      'Opens',
      'Clicks',
      'Bounced',
      'Blocked',
      'Dropped',
      'Unsubscribed',
      'Last Activity',
      'Clicked URLs'
    ];
    
    const rowMapper = (recipient) => [
      recipient.email || '',
      recipient.customerName || '',
      recipient.customerId || '',
      recipient.sent || 0,
      recipient.delivered || 0,
      recipient.opens || 0,
      recipient.clicks || 0,
      recipient.bounced ? 'Yes' : 'No',
      recipient.blocked ? 'Yes' : 'No',
      recipient.dropped ? 'Yes' : 'No',
      recipient.unsubscribed ? 'Yes' : 'No',
      recipient.lastActivity ? recipient.lastActivity.toLocaleString() : '',
      recipient.clickedUrls.join('; ') || ''
    ];
    
    exportData({
      filename: baseFilename,
      format,
      headers,
      data: filteredRecipients,
      rowMapper,
      options: format.toLowerCase() === 'pdf' ? {
        title: `Campaign Analytics - ${campaign.name}`,
        fontSize: 7,
        orientation: 'landscape',
        pageSize: 'a4'
      } : {
        title: `Campaign Analytics - ${campaign.name}`,
        sheetName: 'Campaign Analytics',
        columnWidths: [25, 25, 15, 8, 10, 8, 8, 10, 12, 20, 40]
      }
    });
  };

  if (loading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading analytics...</div>
        </div>
      </main>
    );
  }

  if (!campaign) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        {loadError && <p role="alert" className="text-red-700">Could not load campaign analytics: {loadError}</p>}
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Campaign not found</p>
          <button
            onClick={() => navigate('/email-campaigns')}
            className="text-indigo-600 hover:text-indigo-700"
          >
            ← Back to Campaigns
          </button>
        </div>
      </main>
    );
  }

  const stats = analytics?.stats || {};
  const openRate = stats.sent > 0 ? ((stats.uniqueOpens / stats.sent) * 100).toFixed(1) : 0;
  const clickRate = stats.sent > 0 ? ((stats.uniqueClicks / stats.sent) * 100).toFixed(1) : 0;
  const bounceRate = stats.sent > 0 ? ((stats.bounces / stats.sent) * 100).toFixed(1) : 0;

  // Prepare chart data
  const timelineData = {
    labels: Object.keys(analytics?.timeline || {}).sort(),
    datasets: [
      {
        label: 'Opens',
        data: Object.keys(analytics?.timeline || {}).sort().map(date => analytics.timeline[date].opened),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Clicks',
        data: Object.keys(analytics?.timeline || {}).sort().map(date => analytics.timeline[date].clicked),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const hourlyData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: 'Opens by Hour',
        data: analytics?.opensByHour || [],
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1
      }
    ]
  };

  const deviceData = {
    labels: ['Desktop', 'Mobile', 'Tablet', 'Unknown'],
    datasets: [
      {
        data: [
          analytics?.devices.desktop || 0,
          analytics?.devices.mobile || 0,
          analytics?.devices.tablet || 0,
          analytics?.devices.unknown || 0
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(107, 114, 128, 0.7)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 1
      }
    ]
  };

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/email-campaigns')}
          className="text-indigo-600 hover:text-indigo-700 mb-3 flex items-center"
        >
          ← Back to Campaigns
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{campaign.name}</h2>
            <p className="text-gray-600 mt-1">{campaign.subject}</p>
            <p className="text-sm text-gray-500 mt-1">
              Sent: {campaign.sentAt ? new Date(campaign.sentAt.toDate()).toLocaleString() : 'Not sent yet'}
            </p>
          </div>

          {/* Time Range Filter */}
          <div className="flex space-x-2">
            {['all', '7d', '30d', '60d', '90d'].map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded ${
                  timeRange === range
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {range === 'all' ? 'All Time' : `Last ${range.replace('d', ' Days')}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Failure Banner */}
      {(campaign.status === 'failed' || campaign.status === 'partial_failure' || campaign.error) && (
        <div className={`mb-6 p-4 rounded-lg border ${
          campaign.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">{campaign.status === 'failed' ? '❌' : '⚠️'}</span>
            <h4 className={`font-semibold ${campaign.status === 'failed' ? 'text-red-900' : 'text-orange-900'}`}>
              {campaign.status === 'failed' ? 'Campaign Failed to Send' : 'Campaign Partially Failed'}
            </h4>
          </div>
          {campaign.error && (
            <p className={`text-sm ${campaign.status === 'failed' ? 'text-red-700' : 'text-orange-700'}`}>{campaign.error}</p>
          )}
          {campaign.failureDetails && (
            <details className="mt-2">
              <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">Show failure details</summary>
              <div className="mt-1 text-xs space-y-1">
                {Object.entries(campaign.failureDetails).map(([reason, count]) => (
                  <div key={reason} className="flex justify-between">
                    <span className="text-gray-700">{reason}</span>
                    <span className="text-red-600 font-medium">{count} emails</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Sent</div>
          <div className="text-2xl font-bold text-gray-900">{stats.sent?.toLocaleString() || 0}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: 95-98%">
            Delivered
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{stats.delivered?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">
            {stats.sent > 0 ? ((stats.delivered / stats.sent) * 100).toFixed(1) : 0}%
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Emails that failed to send via SendGrid">
            Failed
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className={`text-2xl font-bold ${(stats.failed || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {stats.failed?.toLocaleString() || 0}
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Unique recipients with human opens — bot/scanner opens excluded. Industry benchmark: 20-25%">
            Unique Opens
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">{stats.uniqueOpens?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">
            {openRate}% unique recipient rate
          </div>
          {stats.uniqueMachineOpens > 0 && (
            <div
              className="text-xs text-gray-400 mt-1 cursor-help"
              title="Opens excluded as likely bot/security-scanner activity (missing/generic user-agent, burst pattern, or known automation signature). Shown for transparency — if a specific recipient looks miscategorized, check the recipient table below."
            >
              + {stats.uniqueMachineOpens.toLocaleString()} filtered (bot/scanner)
            </div>
          )}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Unique recipients with human clicks. Industry benchmark: 2-5%">
            Unique Clicks
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-indigo-600">{stats.uniqueClicks?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">{clickRate}% unique recipient rate</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: <2%">
            Bounces
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-red-600">{stats.bounces?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">{bounceRate}% rate</div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Blocked: SendGrid attempted delivery but the recipient's mail server rejected it (e.g. blacklist/RBL). Dropped: SendGrid never attempted delivery (suppression list, invalid address, prior bounce/spam report). Pending Retry: deferred recipients still within SendGrid's 72h retry window. These match SendGrid's own Activity Feed categories.">
            Blocked / Dropped / Pending Retry
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className={`text-2xl font-bold ${(stats.blocked > 0 || stats.dropped > 0 || stats.deferred > 0) ? 'text-amber-600' : 'text-gray-400'}`}>
            {(stats.blocked || 0).toLocaleString()} / {(stats.dropped || 0).toLocaleString()} / {(stats.deferred || 0).toLocaleString()}
          </div>
          {stats.expiredDeferred > 0 && (
            <div className="text-xs text-gray-500 mt-1" title="Recipients deferred more than 72 hours ago without confirmed delivery. SendGrid has stopped retrying these.">
              + {stats.expiredDeferred.toLocaleString()} expired
            </div>
          )}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: <0.5%">
            Unsubscribes
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">{stats.unsubscribes?.toLocaleString() || 0}</div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Timeline Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Over Time</h3>
          <Line
            data={timelineData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { position: 'top' },
                tooltip: { mode: 'index', intersect: false }
              },
              scales: {
                y: { beginAtZero: true }
              }
            }}
          />
        </div>

        {/* Device Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h3>
          <div className="flex items-center justify-center">
            <div style={{ maxWidth: '300px', maxHeight: '300px' }}>
              <Doughnut
                data={deviceData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Opens by Hour */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Opens by Hour of Day</h3>
          <Bar
            data={hourlyData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { display: false }
              },
              scales: {
                y: { beginAtZero: true }
              }
            }}
          />
        </div>

        {/* Top Links */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Clicked Links</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {analytics?.topLinks && analytics.topLinks.length > 0 ? (
              analytics.topLinks.map((link, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-grow mr-4">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-700 truncate block"
                      title={link.url}
                    >
                      {link.url.length > 50 ? link.url.substring(0, 50) + '...' : link.url}
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold text-gray-900">{link.clicks}</span>
                    <span className="text-xs text-gray-500">clicks</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No link clicks recorded yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Engaged Customers and Email Addresses Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top 10 Customers by Engagement in this Campaign */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Most Engaged Customers</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {analytics?.topCustomers && analytics.topCustomers.length > 0 ? (
              analytics.topCustomers.map((customer, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center space-x-3 flex-grow mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-grow">
                      <div className="text-sm font-medium truncate">
                        {customer.customerId ? (
                          <button onClick={() => navigate(`/customer/${customer.customerId}`)} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">{customer.customerName}</button>
                        ) : (
                          <span className="text-gray-900">{customer.customerName}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {customer.opens} opens • {customer.clicks} clicks
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right">
                      <div className="text-lg font-bold text-indigo-600">{customer.totalEngagement}</div>
                      <div className="text-xs text-gray-500">interactions</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No customer engagement data yet
              </div>
            )}
          </div>
        </div>

        {/* Top 10 Email Addresses by Engagement in this Campaign */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Most Engaged Email Addresses</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {analytics?.topEmails && analytics.topEmails.length > 0 ? (
              analytics.topEmails.map((emailData, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center space-x-3 flex-grow mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="text-sm font-medium truncate" title={emailData.email}>
                        {emailData.customerId ? (
                          <button onClick={() => navigate(`/customer/${emailData.customerId}`)} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">{emailData.email}</button>
                        ) : (
                          <span className="text-gray-900">{emailData.email}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate" title={emailData.customerName}>
                        {emailData.customerId ? (
                          <button onClick={() => navigate(`/customer/${emailData.customerId}`)} className="hover:text-indigo-600 hover:underline text-left">{emailData.customerName}</button>
                        ) : emailData.customerName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {emailData.opens} opens • {emailData.clicks} clicks
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">{emailData.totalEngagement}</div>
                      <div className="text-xs text-gray-500">interactions</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No email engagement data yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="flex justify-end mb-6">
        <ExportButton
          onExport={handleExport}
          formats={['csv', 'xlsx', 'pdf']}
          disabled={false}
          tooltipText="Export campaign analytics in various formats"
          label="Export Analytics"
          className="bg-indigo-600 hover:bg-indigo-700"
        />
      </div>

      {/* Recipients Table */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recipients Engagement</h3>
        
        {/* Search and Filter Controls */}
        <div className="flex items-center space-x-4 mb-4">
          <input
            type="text"
            placeholder="Search by email or customer..."
            value={recipientSearch}
            onChange={(e) => setRecipientSearch(e.target.value)}
            className={`flex-grow border rounded px-3 py-2 ${
              recipientSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
            }`}
          />
          
          <select
            value={engagementFilter}
            onChange={(e) => setEngagementFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="all">All Recipients</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="bounced">Bounced</option>
            <option value="blocked">Blocked</option>
            <option value="dropped">Dropped</option>
            <option value="unopened">Unopened</option>
          </select>
          
          <div className="text-sm text-gray-600">
            {filteredRecipients.length} of {recipientData.length} recipients
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th
                  onClick={() => handleSort('email')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('customerName')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Customer {sortField === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('opens')}
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Opens {sortField === 'opens' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('clicks')}
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Clicks {sortField === 'clicks' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th
                  onClick={() => handleSort('lastActivity')}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Last Activity {sortField === 'lastActivity' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecipients.length > 0 ? (
                filteredRecipients.map((recipient, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {recipient.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {recipient.customerName || <span className="text-gray-400">N/A</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`font-semibold ${recipient.opens > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                        {recipient.opens}
                      </span>
                      {recipient.machineOpens > 0 && (
                        <span
                          className="ml-1 text-xs text-gray-400 cursor-help"
                          title={`${recipient.machineOpens} open(s) excluded as likely bot/security-scanner activity. If this looks wrong for this recipient, treat their engagement as uncertain rather than confirmed absent.`}
                        >
                          (+{recipient.machineOpens} filtered)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`font-semibold ${recipient.clicks > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                        {recipient.clicks}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center space-x-1">
                        {recipient.bounced && (
                          <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700">Bounced</span>
                        )}
                        {recipient.blocked && (
                          <span className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700">Blocked</span>
                        )}
                        {recipient.dropped && (
                          <span className="px-2 py-1 text-xs rounded bg-orange-200 text-orange-800" title="SendGrid never attempted delivery (e.g. suppression list, invalid address, previous bounce/spam report on file).">Dropped</span>
                        )}
                        {recipient.deferred && !recipient.delivered && !recipient.bounced && !recipient.blocked && (
                          <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700" title="Deferred by recipient server. SendGrid is still auto-retrying (within 72h window).">⏳ Pending Retry</span>
                        )}
                        {recipient.deferredExpired && !recipient.delivered && !recipient.bounced && !recipient.blocked && (
                          <span className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-700" title="Deferred more than 72 hours ago without confirmed delivery. SendGrid has stopped retrying.">Expired</span>
                        )}
                        {recipient.unsubscribed && (
                          <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-700">Unsubscribed</span>
                        )}
                        {!recipient.bounced && !recipient.blocked && !recipient.dropped && !recipient.unsubscribed && recipient.delivered > 0 && (
                          <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">Delivered</span>
                        )}
                        {!recipient.bounced && !recipient.blocked && !recipient.dropped && !recipient.unsubscribed && !recipient.deferred && !recipient.deferredExpired && recipient.delivered === 0 && recipient.sent > 0 && (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">Sent</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {recipient.lastActivity ? recipient.lastActivity.toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No recipients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
};

export default CampaignAnalyticsPage;

